
/* eslint-disable quotes */
import config from "../config/config";
import { UsStateList } from "../resources/UsStateList";
import { BlogInfo, EnumOption, LocationDetails } from "../types/interfaces";
import { Dictionary } from "../types/types";
import { ApiOptions, callApi, fetchApiResult } from "./apiServices";
import { getCurrentLanguage } from "./languageService";
import { readFromStorage, StorageType, writeToStorage } from "./storageServies";
import { LogVerbose } from "./utilities";

// import { Result } from '../types/result';
// import { addQueryParameter } from './utilities';

export type ContentDictionary = Dictionary<string>;
export type PageContents = { [key: string]: ContentDictionary };
export type LocalizedPageContents = { [key: string]: PageContents };

// START: GitHub Copilot Generated Code
// Deduplicate concurrent language prefetches so startup preload and middleware
// do not issue duplicate calls for the same resource/language key.
const getCareNowInFlightRequests = new Map<string, Promise<LocationDetails[] | undefined>>();
const healthBlogInFlightRequests = new Map<string, Promise<BlogInfo[] | undefined>>();
// END: GitHub Copilot Generated Code

export const getUsStateList = async (fromApi?: boolean) => {
  if (!fromApi) {
    return UsStateList;
  }

  return getEnumOptions('state');
};

/**
 * Gets enum options for a specific enum name
 * @param enumName - The name of the enum
 * @param storageType - Optional storage type for caching
 * @param language - Optional language (defaults to Redux store language)
 * @returns Array of enum options
 */
export const getEnumOptions = async (
  enumName: string,
  storageType?: StorageType,
  language?: string,
): Promise<EnumOption[]> => {
  // Get language from parameter or Redux store (single source of truth)
  if (!language) {
    language = getCurrentLanguage();
  }

  LogVerbose(`Loading Enum <${enumName}> in ${language}`);
  const keyName = `EnumOptions_${enumName}_${language}`;
  const cached = readFromStorage<EnumOption[]>(keyName, storageType);
  if (cached) {
    LogVerbose('Using cached enum options for ' + enumName);
    return cached;
  }

  const apiOptions: ApiOptions = {
    url: `${config.apiConfig.baseUrl}/${config.apiConfig.enumOptionsEndPoint}/${enumName}?language=${encodeURIComponent(language)}`,
  };

  try {
    const response = await callApi(apiOptions);
    if (!response || !response.ok) return [];

    const data = (await response.json()) as EnumOption[];
    if (data) writeToStorage(keyName, data, storageType);
    return data;
  } catch (e: unknown) {
    console.error(
      `Failed to get enum options for ${enumName} from ${apiOptions.url}`,
      e,
    );
    return [];
  }
};

export const getEnumDisplayAsync = async (
  enumName: string,
  enumValue: string | null | undefined,
) => {
  if (!enumValue) return null;
  const options = await getEnumOptions(enumName);
  const option = options.find(
    (o) => o.value === enumValue || o.display === enumValue,
  );
  return option?.display;
};

export const getEnumDisplay = (
  options: EnumOption[],
  enumValue: string | null | undefined,
) => {
  if (!enumValue) return undefined;

  const option = options.find(
    (o) => o.value === enumValue || o.display === enumValue,
  );
  return option?.display;
};

/**
 * Load GetCareNow location details
 * @param language - Optional language (defaults to Redux store language)
 * @returns Location details or undefined
 */
export const loadGetCareNowDetails = async (language?: string): Promise<
  LocationDetails[] | undefined
> => {
  // Get language from parameter or Redux store (single source of truth)
  if (!language) {
    language = getCurrentLanguage();
  }

  LogVerbose(`Loading GetCareNow detail in ${language}`);
  const keyName = `GetCareNowDetails_${language}`;
  const cached = readFromStorage<LocationDetails[]>(keyName, 'SessionStorage');
  if (cached) {
    LogVerbose(`Using cached info for GetCareNow in ${language}`);
    return cached;
  }

  // START: GitHub Copilot Generated Code
  const inFlightRequest = getCareNowInFlightRequests.get(keyName);
  if (inFlightRequest) {
    LogVerbose(`Using in-flight GetCareNow request for ${language}`);
    return inFlightRequest;
  }
  // END: GitHub Copilot Generated Code

  const url = `${config.apiConfig.baseUrl}/${config.apiConfig.locationDetailsEndPoint}?language=${encodeURIComponent(language)}&defaultLanguage=${config.languageConfig.defaultLanguageName}`;
  const apiOptions: ApiOptions = {
    url: url,
  };

  // START: GitHub Copilot Generated Code
  const request = (async () => {
    const result = await fetchApiResult<LocationDetails[]>(apiOptions);

    if (!result.ok) {
      return undefined;
    }
    console.log('api result: ', result);

    const locationDetails = result.data!;
    writeToStorage(keyName, locationDetails);

    return locationDetails;
  })();

  getCareNowInFlightRequests.set(keyName, request);

  try {
    return await request;
  } finally {
    getCareNowInFlightRequests.delete(keyName);
  }
  // END: GitHub Copilot Generated Code
};

/**
 * Load health blog information
 * @param language - Optional language (defaults to Redux store language)
 * @returns Blog info or undefined
 */
export const loadHealthBlog = async (language?: string): Promise<BlogInfo[] | undefined> => {
  // Get language from parameter or Redux store (single source of truth)
  if (!language) {
    language = getCurrentLanguage();
  }

  LogVerbose(`Loading Health Blog info in ${language}`);
  const keyName = `HeathBlogInfoDetails_${language}`;
  const cached = readFromStorage<BlogInfo[]>(keyName, 'SessionStorage');
  if (cached) {
    LogVerbose(`Using cached info for Health Blog in ${language}`);
    return cached;
  }

  // START: GitHub Copilot Generated Code
  const inFlightRequest = healthBlogInFlightRequests.get(keyName);
  if (inFlightRequest) {
    LogVerbose(`Using in-flight Health Blog request for ${language}`);
    return inFlightRequest;
  }
  // END: GitHub Copilot Generated Code


  const url = `${config.apiConfig.baseUrl}/${config.apiConfig.healthBlogInfoEndpoint}?language=${encodeURIComponent(language)}&defaultLanguage=${config.languageConfig.defaultLanguageName}`;
  const apiOptions: ApiOptions = {
    url: url,
  };

  // START: GitHub Copilot Generated Code
  const request = (async () => {
    const result = await fetchApiResult<BlogInfo[]>(apiOptions);
    if (!result.ok) {
      return undefined;
    }
    console.log('api result: ', result);

    const healthBlogInfo = result.data!;
    writeToStorage(keyName, healthBlogInfo);

    return healthBlogInfo;
  })();

  healthBlogInFlightRequests.set(keyName, request);

  try {
    return await request;
  } finally {
    healthBlogInFlightRequests.delete(keyName);
  }
  // END: GitHub Copilot Generated Code
};
